import { useCallback, useEffect, useRef, useState } from "react";
import { API, api } from "@/lib/api";

const SESSION_KEY = "md_ai_chat_session";
const STORAGE_KEY = "md_ai_chat_messages";

const SEED = [
  { role: "assistant", content: "Hi! I'm MindDhara's assistant 🌿\nAsk me anything about our therapy, mentorship, pricing or how to book." },
];

function readPersisted() {
  try {
    const cached = localStorage.getItem(STORAGE_KEY);
    return cached ? JSON.parse(cached) : SEED;
  } catch {
    return SEED;
  }
}

/**
 * Encapsulates message history, session id, send/reset and persistence for the
 * MindDhara assistant. Returns plain state + actions so any UI shell can plug
 * into it without knowing about localStorage or session ids.
 */
export function useChatMessages({ scrollerRef } = {}) {
  const [messages, setMessages] = useState(readPersisted);
  const [busy, setBusy] = useState(false);
  const sessionRef = useRef(localStorage.getItem(SESSION_KEY) || "");

  // Persist + auto-scroll on any message change.
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(messages));
    } catch (e) {
      console.warn("[AIChat] message history persist failed:", e?.message || e);
    }
    if (scrollerRef?.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [messages, scrollerRef]);

  const send = useCallback(async (text) => {
    const clean = String(text || "").trim();
    if (!clean || busy) return;
    setMessages((m) => [...m, { role: "user", content: clean }]);
    setBusy(true);
    try {
      // Streaming: tokens render as they arrive for a fast, live response.
      const resp = await fetch(`${API}/chat/stream`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionRef.current || "", message: clean }),
      });
      if (!resp.ok || !resp.body) throw new Error(`stream failed: ${resp.status}`);
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let started = false;
      let acc = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split("\n\n");
        buffer = events.pop();
        for (const ev of events) {
          const line = ev.split("\n").find((l) => l.startsWith("data: "));
          if (!line) continue;
          let d;
          try { d = JSON.parse(line.slice(6)); } catch { continue; }
          if (d.session_id && d.session_id !== sessionRef.current) {
            sessionRef.current = d.session_id;
            localStorage.setItem(SESSION_KEY, d.session_id);
          }
          if (d.error) throw new Error(d.error);
          if (d.delta) {
            acc += d.delta;
            if (!started) {
              started = true;
              setBusy(false); // hide typing dots once first token lands
              setMessages((m) => [...m, { role: "assistant", content: acc }]);
            } else {
              const snapshot = acc;
              setMessages((m) => {
                const next = m.slice();
                next[next.length - 1] = { role: "assistant", content: snapshot };
                return next;
              });
            }
          }
        }
      }
      if (!started) throw new Error("empty stream");
    } catch (e) {
      console.warn("[AIChat] stream failed, falling back:", e?.message || e);
      // Fallback to the non-streaming endpoint.
      try {
        const r = await api.post("/chat/message", { session_id: sessionRef.current || "", message: clean });
        const sid = r.data?.session_id || sessionRef.current;
        if (sid && sid !== sessionRef.current) {
          sessionRef.current = sid;
          localStorage.setItem(SESSION_KEY, sid);
        }
        setMessages((m) => [...m, { role: "assistant", content: r.data?.reply || "Sorry, no reply." }]);
      } catch (e2) {
        console.warn("[AIChat] send failed:", e2?.message || e2);
        setMessages((m) => [
          ...m,
          { role: "assistant", content: "I couldn't reach the assistant right now. Please try again in a moment." },
        ]);
      }
    } finally {
      setBusy(false);
    }
  }, [busy]);

  const reset = useCallback(() => {
    sessionRef.current = "";
    localStorage.removeItem(SESSION_KEY);
    localStorage.removeItem(STORAGE_KEY);
    setMessages(SEED);
  }, []);

  return { messages, busy, send, reset };
}
